import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import {
  createTranslator,
  englishMessages,
  getBrowserInitialLocale,
  loadLocaleMessages,
  persistLocale,
  setDocumentLocale,
} from "./locale";
import type { I18nContextValue, I18nProviderProps, LocaleCode, TranslationDictionary } from "./types";

export const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
  children,
  initialLocale,
  initialMessages,
}: I18nProviderProps) {
  const [locale, setLocaleState] = useState<LocaleCode>(() => initialLocale ?? getBrowserInitialLocale());
  const [messages, setMessages] = useState<TranslationDictionary>(() => initialMessages ?? englishMessages);

  useEffect(() => {
    setDocumentLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (initialMessages && locale === initialLocale) {
      setMessages(initialMessages);
      return;
    }
    let active = true;
    void loadLocaleMessages(locale).then((nextMessages) => {
      if (active) {
        setMessages(nextMessages);
      }
    });
    return () => {
      active = false;
    };
  }, [initialLocale, initialMessages, locale]);

  const setLocale = useCallback((nextLocale: LocaleCode) => {
    persistLocale(nextLocale);
    setLocaleState(nextLocale);
  }, []);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    setLocale,
    t: createTranslator(locale, messages),
  }), [locale, messages, setLocale]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}
